<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { usePlans } from '../../composables/usePlans';
import { useMembershipContext } from '../../composables/useMembershipContext';
import { SubscriptionActionType } from '../../constants';
import { formatPrice } from '../../utils/helpers';
import { PLAN_CONTENT } from '../../content/membershipPlans';

export default {
  name: 'RestartMembershipPage',
  setup() {
    const router = useRouter();
    const store = useStore();
    const isAnnual = ref(false);
    const confirming = ref(false);
    const selectedPlan = ref(null);

    const { plans, isLoading, error: plansError } = usePlans();

    // Use the first subscription id (may be a canceled one surfaced by our getter)
    const subId = computed(() => store.getters.currentSubscriptions?.[0]?.id ?? '');
    const { isLoading: isCreating, error: createError, mutate } = useMembershipContext(subId.value)[SubscriptionActionType.CREATE];

    function getPlan(tier) {
      return plans.value.find(
        (p) => p.tier === tier && p.interval === (isAnnual.value ? 'year' : 'month'),
      ) || null;
    }

    function getAnnualPlan(tier) {
      return plans.value.find((p) => p.tier === tier && p.interval === 'year') || null;
    }

    function annualSavingsAmount(tier) {
      const monthly = plans.value.find((p) => p.tier === tier && p.interval === 'month');
      const annual = plans.value.find((p) => p.tier === tier && p.interval === 'year');
      if (!monthly || !annual) return 0;
      return (monthly.amount * 12) - annual.amount;
    }

    function getAnnualSavingsLabel(tier) {
      const savings = annualSavingsAmount(tier);
      if (!savings) return null;
      const monthly = plans.value.find((p) => p.tier === tier && p.interval === 'month');
      const rounded = Math.round(savings / 100) * 100; // round to nearest dollar
      return `Save ${formatPrice(rounded, monthly?.currency ?? 'usd')} per year`;
    }

    function selectPlan(tier) {
      selectedPlan.value = getPlan(tier);
      confirming.value = true;
    }

    async function handleCreate() {
      await mutate(selectedPlan.value.priceId);
      if (!createError.value) {
        router.push({ name: 'Membership' });
      }
    }

    return {
      isAnnual,
      confirming,
      selectedPlan,
      isLoading,
      plansError,
      isCreating,
      createError,
      getPlan,
      getAnnualPlan,
      annualSavingsAmount,
      getAnnualSavingsLabel,
      selectPlan,
      handleCreate,
      formatPrice,
      PLAN_CONTENT,
    };
  },
};
</script>

<template>
  <main class="restart-page">
    <!-- Confirm step overlay -->
    <div v-if="confirming" class="restart-page__confirm-overlay">
      <div class="restart-page__confirm-box">
        <h2 class="restart-page__confirm-title">Confirm Membership</h2>
        <p class="restart-page__confirm-copy">
          You're restarting with the
          <strong>{{ selectedPlan.name }}</strong> plan,
          billed {{ selectedPlan.interval === 'year' ? 'annually' : 'monthly' }} at
          <strong>{{ formatPrice(selectedPlan.amount, selectedPlan.currency) }}</strong>
          (plus applicable taxes).
        </p>
        <p v-if="createError" class="restart-page__error" v-html="createError" />
        <div class="restart-page__confirm-actions">
          <button class="restart-page__btn button-layout button-outline" @click="confirming = false">
            Back
          </button>
          <button
            class="restart-page__btn button-layout button-cta"
            :disabled="isCreating"
            @click="handleCreate"
          >
            <font-awesome-icon icon="spinner" spin v-if="isCreating" />
            <span v-else>Confirm</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Back nav -->
    <router-link :to="{ name: 'Membership' }" class="restart-page__back">
      ← Back to Account
    </router-link>

    <!-- Header -->
    <div class="restart-page__header">
      <h1 class="restart-page__title">
        Restart Your <span class="restart-page__title--accent">Journey</span>
      </h1>
      <p class="restart-page__subtitle">
        Pick your plan. Put in the work. Every punch tracked and every round counted, no matter where you train.
        Choose your membership tier and billing cycle below.
      </p>
    </div>

    <!-- Loading / error -->
    <div v-if="isLoading" class="restart-page__loading">
      <font-awesome-icon icon="spinner" size="2x" spin />
    </div>
    <p v-else-if="plansError" class="restart-page__error restart-page__error--center">
      {{ plansError }}
    </p>

    <template v-else>
      <!-- Billing toggle -->
      <div class="restart-page__toggle-heading">Step 1 — Choose your billing cycle</div>
      <div class="restart-page__toggle-row">
        <span class="restart-page__toggle-label" :class="{ 'restart-page__toggle-label--active': !isAnnual }">
          Monthly
        </span>
        <button
          class="restart-page__toggle"
          :class="{ 'restart-page__toggle--on': isAnnual }"
          @click="isAnnual = !isAnnual"
          role="switch"
          :aria-checked="isAnnual"
        >
          <span class="restart-page__toggle-knob" />
        </button>
        <span class="restart-page__toggle-label" :class="{ 'restart-page__toggle-label--active': isAnnual }">
          Annual
        </span>
      </div>

      <!-- Plan cards -->
      <div class="restart-page__step-heading">Step 2 — Choose your membership tier</div>
      <div class="restart-page__cards">
        <div
          v-for="(content, tier) in PLAN_CONTENT"
          :key="tier"
          class="restart-page__card"
        >
          <div v-if="getAnnualSavingsLabel(tier)" class="restart-page__card-savings">
            {{ getAnnualSavingsLabel(tier) }}
          </div>

          <h2 class="restart-page__card-name">{{ content.name }}</h2>

          <template v-if="getPlan(tier)">
            <div class="restart-page__card-price">
              <span class="restart-page__card-amount">{{ formatPrice(getPlan(tier).amount, getPlan(tier).currency) }}</span>
              <span class="restart-page__card-per">&nbsp;/ {{ isAnnual ? 'year' : 'month' }}</span>
            </div>
            <p v-if="!isAnnual && getAnnualPlan(tier)" class="restart-page__card-annual-note">
              Or {{ formatPrice(getAnnualPlan(tier).amount, getAnnualPlan(tier).currency) }}/year.
              Save {{ formatPrice(Math.round(annualSavingsAmount(tier) / 100) * 100, getPlan(tier).currency) }}.
            </p>
          </template>

          <p class="restart-page__card-tagline">{{ content.tagline }}</p>

          <div class="restart-page__card-divider" />

          <p v-if="content.featuresHeader" class="restart-page__card-features-header">
            {{ content.featuresHeader }}
          </p>
          <ul class="restart-page__card-features">
            <li v-for="feature in content.features" :key="feature">
              <span class="restart-page__card-check">✓</span>
              {{ feature }}
            </li>
          </ul>

          <button
            class="restart-page__card-btn button-layout button-outline"
            :disabled="!getPlan(tier)"
            @click="selectPlan(tier)"
          >
            Select {{ content.name }}
          </button>
        </div>
      </div>

      <!-- Footer trust bar -->
      <p class="restart-page__fine-print">
        Membership starts immediately upon selection. If you have existing hardware, please ensure trackers are charged and the app is updated to the latest version.
      </p>
    </template>
  </main>
</template>

<style lang="scss" scoped>
.restart-page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: $fc-greyscale-700;
    text-decoration: none;
    margin-bottom: 1.5rem;

    &:hover { color: $color-black; }
  }

  &__header {
    margin-bottom: 2rem;
  }

  &__title {
    font-family: $font-stack-heading;
    font-size: clamp(2rem, 5vw, 3rem);
    text-transform: uppercase;
    line-height: 1;
    margin: 0 0 0.75rem;
    color: $color-black;

    &--accent { color: $color-red; }
  }

  &__subtitle {
    font-size: 0.95rem;
    color: $fc-greyscale-800;
    max-width: 480px;
    margin: 0;
    line-height: 1.5;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 4rem 0;
    color: $fc-greyscale-600;
  }

  &__error {
    color: $color-red;
    font-size: 0.875rem;
    margin-bottom: 1rem;

    &--center { text-align: center; padding: 2rem 0; }
  }
  &__toggle-heading,
  &__step-heading {
    font-family: $font-stack-heading;
    font-size: 1rem;
    text-transform: uppercase;
    color: $color-black;
    margin-bottom: 0.6rem;
  }
  // ── Toggle ────────────────────────────────────────────
  &__toggle-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }

  &__toggle-label {
    font-size: 0.8rem;
    text-transform: uppercase;
    color: $fc-greyscale-500;
    transition: color 0.15s ease;

    &--active { color: $color-black; }
  }

  &__toggle {
    position: relative;
    width: 48px;
    height: 26px;
    background: $fc-greyscale-400;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s ease;

    &--on { background: $color-black; }
  }

  &__toggle-knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    background: $color-white;
    border-radius: 50%;
    transition: transform 0.2s ease;

    .restart-page__toggle--on & { transform: translateX(22px); }
  }

  // ── Cards ─────────────────────────────────────────────
  &__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2.5rem;

    @media #{$tablet-up} {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__card {
    background: $fc-greyscale-100;
    border: 1px solid $fc-greyscale-300;
    border-radius: 4px;
    padding: 2rem 2rem 1.75rem;
    display: flex;
    flex-direction: column;
  }

  &__card-savings {
    display: inline-block;
    align-self: flex-start;
    border: 1.5px solid $color-black;
    border-radius: 999px;
    font-size: 0.65rem;
    text-transform: uppercase;
    padding: 0.3rem 0.85rem;
    margin-bottom: 1.25rem;
    color: $color-black;
  }

  &__card-name {
    font-family: $font-stack-heading;
    font-size: 2rem;
    text-transform: uppercase;
    color: $color-black;
    margin: 0 0 0.5rem;
    line-height: 1;
  }

  &__card-price {
    display: flex;
    align-items: baseline;
    margin-bottom: 0.2rem;
  }

  &__card-amount {
    font-family: $font-stack-heading;
    font-size: 2.25rem;
    color: $color-red;
    line-height: 1;
  }

  &__card-per {
    font-size: 0.9rem;
    color: $fc-greyscale-700;
  }

  &__card-annual-note {
    font-size: 0.8rem;
    color: $fc-greyscale-700;
    margin: 0 0 0.75rem;
  }

  &__card-tagline {
    font-size: 0.875rem;
    color: $fc-greyscale-700;
    line-height: 1.55;
    margin: 0.5rem 0 0;
    text-align: center;
  }

  &__card-divider {
    height: 1px;
    background: $fc-greyscale-300;
    margin: 1.25rem 0;
  }

  &__card-features-header {
    font-size: 1rem;
    font-weight: 600;
    color: $color-black;
    margin: 0 0 0.25rem;
  }

  &__card-features {
    list-style: none;
    padding: 0;
    margin: 0 0 1.75rem;
    flex: 1;

    li {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.875rem;
      color: $color-black;
      padding: 0.6rem 0;
      border-bottom: 1px solid $fc-greyscale-300;
    }
  }

  &__card-check {
    color: $color-red;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  &__card-btn {
    width: 100%;
    margin-top: auto;

    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  // ── Footer ────────────────────────────────────────────
  &__trust {
    text-align: center;
    font-size: 0.8rem;
    color: $fc-greyscale-700;
    letter-spacing: 0.04em;
    margin-bottom: 0.5rem;
  }

  &__fine-print {
    text-align: center;
    font-size: 0.75rem;
    color: $fc-greyscale-600;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 520px;
  }

  // ── Confirm overlay ───────────────────────────────────
  &__confirm-overlay {
    position: fixed;
    inset: 0;
    background: rgba($color-black, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 1rem;
  }

  &__confirm-box {
    background: $color-white;
    border-radius: 4px;
    padding: 2rem;
    max-width: 440px;
    width: 100%;
  }

  &__confirm-title {
    font-family: $font-stack-heading;
    font-size: 1.25rem;
    text-transform: uppercase;
    margin: 0 0 1rem;
  }

  &__confirm-copy {
    font-size: 0.9rem;
    line-height: 1.6;
    color: $fc-greyscale-900;
    margin-bottom: 1.25rem;
  }

  &__confirm-actions {
    display: flex;
    gap: 0.75rem;
  }

  &__btn {
    flex: 1;

    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
}
</style>
